"use client";

/**
 * usePageSearchState — 목록 페이지의 필터/정렬/페이지네이션 상태를 URL(주소창)에 동기화한다.
 * zod에 의존하지 않는다 — _filters(기본값 객체)의 각 값 타입(배열이면 배열, 아니면 단일값)을
 * 보고 화이트리스트와 캐스팅을 판단한다.
 *
 * 브라우저 뒤로가기/새로고침/공유 링크로 들어와도 같은 필터 상태를 그대로 복원한다.
 * (참고: 이 URL 포맷은 options[key]가 아니라 key=value 평평한 형식이다. 실제 API 요청은
 * axios가 filters 객체를 별도로 직렬화해서 보내므로 이 주소창 URL과 포맷이 달라도 무방하다.)
 *
 * @example
 * const initFilters: BaseFilter = {
 *   keyword: "", keywordType: "", startDate: null, endDate: null, dateType: "",
 *   options: { status: [] as string[] },
 * };
 *
 * const {
 *   pagination, sort, filters,
 *   setPaginationAndSync, setFiltersAndSync, setSortAndSync,
 *   resetFilters, // 필터+페이지+정렬을 한 번에 초기 상태로 되돌리고 URL도 한 번만 갱신한다
 * } = usePageSearchState({ defaultPageSize: 20, _filters: initFilters });
 * // 정렬까지 초기값으로 되돌리고 싶으면 defaultSort도 지정
 * // usePageSearchState({ defaultPageSize: 20, _filters: initFilters, defaultSort: { sortKey: "createdAt", sortType: "DESC" } });
 */
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";

export interface PaginationType {
    page: number;
    pageSize: number;
}

export interface SortType {
    sortKey?: string;
    sortType?: "ASC" | "DESC";
}

export interface BaseFilter {
    keyword: string;
    keywordType: string;
    options: Record<string, any>;
    startDate: string | null;
    endDate: string | null;
    dateType: string;
}

export interface UsePageSearchStateConfig {
    defaultPageSize?: number;
    _filters: BaseFilter;
    /** resetFilters() 호출 시 되돌아갈 정렬 상태. 기본은 정렬 없음({}) */
    defaultSort?: SortType;
    params?: Record<string, any>;
}

export const usePageSearchState = (config: UsePageSearchStateConfig) => {
    const { defaultPageSize = 20, _filters, defaultSort = {}, params = {} } = config;
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const { initPagination, initSort, initFilters } = useMemo(() => {
        const page = Number(searchParams.get("page") ?? "1");
        const pageSize = Number(searchParams.get("pageSize") ?? defaultPageSize.toString());
        const sortKey = searchParams.get("sortKey") ?? undefined;
        const sortType = (searchParams.get("sortType") as "ASC" | "DESC" | null) ?? undefined;
        const keyword = searchParams.get("keyword") ?? _filters.keyword;
        const keywordType = searchParams.get("keywordType") ?? _filters.keywordType;
        const startDate = searchParams.get("startDate") ?? _filters.startDate;
        const endDate = searchParams.get("endDate") ?? _filters.endDate;
        const dateType = searchParams.get("dateType") ?? _filters.dateType;
        const optionsFromQuery = { ..._filters.options };
        Object.keys(_filters.options || {}).forEach((key) => {
            const defaultVal = _filters.options[key];
            if (Array.isArray(defaultVal)) {
                const all = searchParams.getAll(key);
                if (all.length > 0) {
                    optionsFromQuery[key] = all;
                }
            } else {
                const v = searchParams.get(key);
                if (v !== null) {
                    optionsFromQuery[key] = v;
                }
            }
        });
        return {
            initPagination: { page, pageSize } as PaginationType,
            initSort: { sortKey, sortType } as SortType,
            initFilters: {
                keyword,
                keywordType,
                startDate,
                endDate,
                dateType,
                options: optionsFromQuery,
            } as BaseFilter,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, params, _filters, defaultPageSize]);

    const [pagination, setPagination] = useState<PaginationType>(initPagination);
    const [sort, setSort] = useState<SortType>(initSort);
    const [filters, setFilters] = useState<BaseFilter>(initFilters);

    const buildSearchParams = (
        nextPagination: PaginationType,
        nextFilters: BaseFilter,
        nextSort: SortType
    ) => {
        const sp = new URLSearchParams();
        sp.set("page", nextPagination.page.toString());
        sp.set("pageSize", nextPagination.pageSize.toString());

        // filters
        if (nextFilters.keyword) sp.set("keyword", nextFilters.keyword);
        if (nextFilters.keywordType) sp.set("keywordType", nextFilters.keywordType);
        if (nextFilters.dateType) sp.set("dateType", nextFilters.dateType);
        if (nextFilters.startDate) sp.set("startDate", nextFilters.startDate);
        if (nextFilters.endDate) sp.set("endDate", nextFilters.endDate);
        Object.entries(nextFilters.options || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") return;
            if (Array.isArray(value)) {
                value.forEach((v) => sp.append(key, String(v)));
            } else {
                sp.set(key, String(value));
            }
        });
        // sort
        if (nextSort.sortKey) sp.set("sortKey", nextSort.sortKey);
        if (nextSort.sortType) sp.set("sortType", nextSort.sortType);
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null || value === "") return;
                if (Array.isArray(value)) {
                    value.forEach((v) => sp.append(key, String(v)));
                } else {
                    sp.set(key, String(value));
                }
            });
        }
        return sp;
    };

    const syncUrl = (
        nextPagination: PaginationType,
        nextFilters: BaseFilter,
        nextSort: SortType
    ) => {
        const sp = buildSearchParams(nextPagination, nextFilters, nextSort);
        const href = `${pathname}?${sp.toString()}`;
        router.replace(href, { scroll: false });
    };

    const setPaginationAndSync = (
        updater: PaginationType | ((prev: PaginationType) => PaginationType)
    ) => {
        const next =
            typeof updater === "function"
                ? (updater as (prev: PaginationType) => PaginationType)(pagination)
                : updater;
        setPagination(next);
        syncUrl(next, filters, sort);
    };

    const setFiltersAndSync = (
        updater: BaseFilter | ((prev: BaseFilter) => BaseFilter)
    ) => {
        const next =
            typeof updater === "function"
                ? (updater as (prev: BaseFilter) => BaseFilter)(filters)
                : updater;
        setFilters(next);
        syncUrl(pagination, next, sort);
    };

    const setSortAndSync = (updater: SortType | ((prev: SortType) => SortType)) => {
        const next =
            typeof updater === "function"
                ? (updater as (prev: SortType) => SortType)(sort)
                : updater;
        setSort(next);
        syncUrl(pagination, filters, next);
    };

    // 필터 + 페이지 + 정렬을 한 번에 되돌리고, syncUrl은 딱 한 번만 호출한다.
    // (resetFilters 따로 호출 + setSort/syncUrl 따로 또 호출하면 router.replace가 두 번 일어나서
    //  API 요청도 두 번 나간다 — 그걸 방지하려고 여기서 한 번에 처리한다.)
    const resetFilters = () => {
        const reset: BaseFilter = { ..._filters, options: _filters.options };
        const resetPagination = { page: 1, pageSize: pagination.pageSize };
        setFilters(reset);
        setPagination(resetPagination);
        setSort(defaultSort);
        syncUrl(resetPagination, reset, defaultSort);
    };

    return {
        pagination,
        sort,
        filters,

        // state만 바꾸고 싶을 때
        setPagination,
        setFilters,
        setSort,

        // state + URL 같이 바꾸기용
        setPaginationAndSync,
        setFiltersAndSync,
        setSortAndSync,

        resetFilters,
        syncUrl,
    };
};
